import { ReactNode } from "react"
import {
    CategoryBadge,
    DetailLabel,
    DetailList,
    DetailRow,
    DetailValue,
    Thumbnail,
    ThumbnailGrid,
    ThumbnailWrapper
} from "../styled"
import { LostItemCategory } from "@/hooks/useLostItemReport"
import { useTranslation } from "@/hooks/useTranslation"

export interface DetailPhoto {
    id: string
    // 表示用の URL（保存済みなら imageUrl、撮影中なら objectURL）
    url: string
}

interface Props {
    category: LostItemCategory
    itemDescription: string
    storagePlace: string
    note?: string | null
    photos: DetailPhoto[]
    // 保存済みの写真だけタップで拡大させる（撮影中の写真は削除操作と競合するため渡さない）
    onOpenPhoto?: (photo: DetailPhoto) => void
    // 報告日時など、項目の後ろに足したい要素
    footer?: ReactNode
}

/**
 * 忘れ物1件の報告内容。確認画面と完了画面で同じ並び・同じ見た目にするための共通表示。
 * 項目の順序は カテゴリ → 物品の説明 → 保管場所 → 備考 → 写真 で固定する。
 */
export const ReportDetail = ({
    category,
    itemDescription,
    storagePlace,
    note,
    photos,
    onOpenPhoto,
    footer
}: Props) => {
    const { t } = useTranslation()

    return (
        <>
            <CategoryBadge valuable={category === "valuable"}>
                {category === "valuable" ? t("貴重品") : t("一般品")}
            </CategoryBadge>

            <DetailList>
                <DetailRow>
                    <DetailLabel>{t("物品の説明")}</DetailLabel>
                    <DetailValue>{itemDescription}</DetailValue>
                </DetailRow>
                <DetailRow>
                    <DetailLabel>{t("保管場所")}</DetailLabel>
                    <DetailValue>{storagePlace}</DetailValue>
                </DetailRow>
                {note?.trim() && (
                    <DetailRow>
                        <DetailLabel>{t("備考")}</DetailLabel>
                        <DetailValue>{note}</DetailValue>
                    </DetailRow>
                )}
            </DetailList>

            {photos.length > 0 && (
                <ThumbnailGrid>
                    {photos.map(photo =>
                        onOpenPhoto ? (
                            <ThumbnailWrapper
                                key={photo.id}
                                role="button"
                                tabIndex={0}
                                aria-label={t("写真を拡大表示")}
                                onClick={() => onOpenPhoto(photo)}
                                onKeyDown={e => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault()
                                        onOpenPhoto(photo)
                                    }
                                }}
                            >
                                <Thumbnail src={photo.url} alt="" />
                            </ThumbnailWrapper>
                        ) : (
                            <ThumbnailWrapper key={photo.id}>
                                <Thumbnail src={photo.url} alt="" />
                            </ThumbnailWrapper>
                        )
                    )}
                </ThumbnailGrid>
            )}

            {footer}
        </>
    )
}
